import * as React from "react";
import styled from "@emotion/styled";
import { useAuth } from "features/auth";
import { useLocation } from "react-router-dom";
import { Button, FlexRow, Link, Stack, Text, Tooltip } from "atoms";
import { trackEvent } from "contexts/mixpanel";
import { useFollows, useFollow } from "../api/useFollows";

const trackSignUp = () =>
  trackEvent("Button Click", {
    ButtonName: "Sign Up",
    ButtonContext: "Follow Invite Banner",
    Location: window.location.href,
  });
const trackSignIn = () =>
  trackEvent("Button Click", {
    ButtonName: "Sign In",
    ButtonContext: "Follow Button",
    Location: window.location.href,
  });

type FollowButtonProps = {
  entityId: string;
  small?: boolean;
  followText?: React.ReactNode;
  unfollowText?: React.ReactNode;
} & Pick<React.ComponentProps<typeof Button>, "size" | "variant">;

function FollowButton({
  entityId,
  small,
  size,
  followText = "Follow",
  unfollowText = "Following",
  ...props
}: FollowButtonProps) {
  const { user } = useAuth();
  const location = useLocation();
  const { data: artistFollows } = useFollows();
  const { follow, unfollow, isLoading } = useFollow({ artistId: entityId });

  const isFollowing = React.useMemo(
    () => !!artistFollows?.artistIds?.includes(entityId),
    [artistFollows, entityId]
  );

  const onClick = isFollowing ? unfollow : follow;

  return !user ? (
    <STooltip
      positionX="center"
      label={
        <SButton as="div" variant="primary" small={small} size={size}>
          {followText}
        </SButton>
      }
      {...props}
    >
      <Stack gap="sm">
        <Stack gap="xs">
          <Text font="display" textTransform="uppercase">
            Create an Account to&nbsp;follow&nbsp;Artists
          </Text>
          <Text size="xxs" style={{ lineHeight: 1.15 }}>
            Get the latest updates on drops, announcements, and&nbsp;more!
          </Text>
        </Stack>
        <FlexRow gap="10px">
          <Link
            variant="primary"
            size="xxs"
            to={`/register?artistId=${entityId}`}
            state={{ from: location }}
            style={{ marginRight: "-25px" }}
            onClick={trackSignUp}
          >
            Create Account
          </Link>
          <Link
            variant="primary"
            size="xxs"
            to="/sign-in"
            state={{ from: location }}
            onClick={trackSignIn}
          >
            Sign in
          </Link>
        </FlexRow>
      </Stack>
    </STooltip>
  ) : (
    <SButton
      variant={isFollowing ? "secondary" : "primary"}
      aria-label={isFollowing ? "Unfollow" : "Follow"}
      onClick={onClick}
      disabled={isLoading}
      small={small}
      size={size}
      {...props}
    >
      {isFollowing ? unfollowText : followText}
    </SButton>
  );
}

const STooltip = styled(Tooltip)({
  ".TooltipContent": {
    maxWidth: 280,
    "div, a": {
      marginRight: "0 !important",
    },
  },
});

const SButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "small",
})<{ small?: boolean }>(({ small }) => ({
  ...(small && {
    fontSize: 12,
    padding: "1px .6em",
  }),
}));

export { FollowButton };
